/** 阿里云 OSS 开通与配置：官方入口（新窗口打开） */
export type AliyunGuideLink = {
  id: string;
  label: string;
  url: string;
  hint?: string;
};

export const ALIYUN_OSS_LINKS: AliyunGuideLink[] = [
  {
    id: 'register',
    label: '注册阿里云账号',
    url: 'https://account.aliyun.com/register/registration.htm',
    hint: '未开通阿里云时从此开始',
  },
  {
    id: 'oss_console',
    label: 'OSS 控制台',
    url: 'https://oss.console.aliyun.com/',
    hint: '创建 Bucket、查看 Endpoint',
  },
  {
    id: 'oss_bucket',
    label: '创建 Bucket',
    url: 'https://oss.console.aliyun.com/bucket',
    hint: '对外访问须选「公共读」或「公共读私有写」',
  },
  {
    id: 'oss_endpoint_doc',
    label: '地域与 Endpoint 对照',
    url: 'https://help.aliyun.com/zh/oss/user-guide/regions-and-endpoints',
    hint: '填写表单「OSS Endpoint」时查阅',
  },
  {
    id: 'ram_ak',
    label: 'AccessKey 管理（RAM）',
    url: 'https://ram.console.aliyun.com/manage/ak',
    hint: '创建 AccessKey ID / Secret，填入贴图虾表单',
  },
  {
    id: 'ram_user',
    label: '创建 RAM 子账号（推荐）',
    url: 'https://ram.console.aliyun.com/users/create',
    hint: '仅授予目标 Bucket 的 OSS 读写权限',
  },
  {
    id: 'wechat_mp',
    label: '微信小程序后台（download 域名）',
    url: 'https://mp.weixin.qq.com/',
    hint: '添加 OSS 访问域名（见 Bucket 概览中的外网 Endpoint）',
  },
];

export type AliyunSetupStep = {
  order: number;
  title: string;
  body: string;
  linkIds: string[];
  formFields?: string[];
};

export const ALIYUN_OSS_SETUP_STEPS: AliyunSetupStep[] = [
  {
    order: 1,
    title: '注册并实名认证',
    body: '完成阿里云注册与实名认证后，开通对象存储 OSS（按量计费即可）。',
    linkIds: ['register'],
  },
  {
    order: 2,
    title: '创建 OSS Bucket',
    body:
      '新建 Bucket，记录名称与地域 Endpoint（如 oss-cn-hangzhou.aliyuncs.com）。贴图虾对外图片链接使用 OSS 地址，Bucket 须设为「公共读」或「公共读私有写」，否则小程序/浏览器无法直接打开图片。资源组、标签可不填。',
    linkIds: ['oss_bucket', 'oss_console', 'oss_endpoint_doc'],
    formFields: ['endpoint', 'bucket'],
  },
  {
    order: 3,
    title: '创建 AccessKey（建议 RAM 子账号）',
    body:
      '若提示勿用主账号密钥，请使用 RAM 子用户并仅授权目标 Bucket。创建 AccessKey 后复制 ID 与 Secret（Secret 只显示一次），填入贴图虾 OSS 参数页。',
    linkIds: ['ram_user', 'ram_ak'],
    formFields: ['access_key_id', 'access_key_secret'],
  },
  {
    order: 4,
    title: '微信小程序合法域名（如有小程序）',
    body: '在微信公众平台 → 开发管理 → 服务器域名 → download 合法域名，添加 OSS 外网访问域名（Bucket 概览中可见，勿带 https:// 路径）。',
    linkIds: ['wechat_mp'],
  },
  {
    order: 5,
    title: '在贴图虾保存并测试',
    body: '填写 Endpoint、Bucket、AccessKey → 保存配置 → 测试连接 → 一键上传历史图片 → 切换为 OSS 模式。',
    linkIds: [],
  },
];

export const ALIYUN_RAM_OSS_POLICY_EXAMPLE = `{
  "Version": "1",
  "Statement": [
    {
      "Effect": "Allow",
      "Action": [
        "oss:PutObject",
        "oss:GetObject",
        "oss:HeadObject",
        "oss:DeleteObject"
      ],
      "Resource": [
        "acs:oss:*:*:your-bucket-name/*"
      ]
    }
  ]
}`;

export function aliyunLinksByIds(ids: string[]): AliyunGuideLink[] {
  const map = new Map(ALIYUN_OSS_LINKS.map((l) => [l.id, l]));
  return ids.map((id) => map.get(id)).filter((l): l is AliyunGuideLink => l != null);
}

export function aliyunLinkById(id: string): AliyunGuideLink | undefined {
  return ALIYUN_OSS_LINKS.find((l) => l.id === id);
}

export const OSS_GUIDE_PATH = '/oss/guide';

const OSS_GUIDE_WINDOW = 'wes_oss_aliyun_guide';

export function openOssAliyunGuideWindow(): void {
  const base = (import.meta.env.BASE_URL || '/').replace(/\/$/, '') || '';
  const url = `${window.location.origin}${base}${OSS_GUIDE_PATH}`;
  const w = Math.min(960, window.screen.availWidth - 48);
  const h = Math.min(880, window.screen.availHeight - 48);
  const left = Math.max(0, Math.round((window.screen.availWidth - w) / 2));
  const top = Math.max(0, Math.round((window.screen.availHeight - h) / 2));
  const features = [
    `width=${w}`,
    `height=${h}`,
    `left=${left}`,
    `top=${top}`,
    'scrollbars=yes',
    'resizable=yes',
  ].join(',');
  const opened = window.open(url, OSS_GUIDE_WINDOW, features);
  if (!opened) {
    window.open(url, '_blank', 'noopener,noreferrer');
  }
}
